import { useEffect, useState } from "react";
import { CheckCircle2, ImagePlus, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

export default function OrderReviewsCard({ order }) {
  const [reviewed, setReviewed] = useState({});
  const [forms, setForms] = useState({});
  const [photos, setPhotos] = useState({});
  const [sending, setSending] = useState("");

  useEffect(() => {
    api.get("/my/reviews").then(({ data }) => {
      const map = {};
      (data || []).forEach((r) => { map[r.product_id] = r; });
      setReviewed(map);
    }).catch(() => {});
  }, []);

  const setForm = (pid, patch) =>
    setForms((f) => ({ ...f, [pid]: { rating: 5, comment: "", ...f[pid], ...patch } }));

  const submit = async (item) => {
    const form = forms[item.product_id] || { rating: 5, comment: "" };
    if ((form.comment || "").trim().length < 3) {
      toast.error("Écrivez un petit commentaire (3 caractères minimum)");
      return;
    }
    setSending(item.product_id);
    try {
      let photo_path = "";
      if (photos[item.product_id]) {
        const fd = new FormData();
        fd.append("file", photos[item.product_id]);
        const { data: up } = await api.post("/reviews/photo", fd);
        photo_path = up.path;
      }
      const { data } = await api.post("/reviews", {
        product_id: item.product_id, rating: form.rating || 5, comment: form.comment.trim(), photo_path,
      });
      setReviewed((m) => ({ ...m, [item.product_id]: data }));
      setPhotos((p) => ({ ...p, [item.product_id]: null }));
      toast.success("Merci ! Votre avis a été publié.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending("");
    }
  };

  const items = order.items || [];
  if (!items.length) return null;

  return (
    <div className="rounded-xl border border-line bg-white p-4" data-testid="order-reviews-card">
      <p className="flex items-center gap-2 text-sm font-bold text-ink">
        <Star className="h-4 w-4 fill-amber-400 text-amber-400" /> Donnez votre avis
      </p>
      <p className="mt-0.5 text-[11px] text-slate-500">
        Votre avis compte : partagez votre expérience sur les produits reçus.
      </p>
      <div className="mt-3 space-y-3">
        {items.map((item) => {
          const existing = reviewed[item.product_id];
          const form = forms[item.product_id] || { rating: 5, comment: "" };
          return (
            <div key={item.product_id} className="rounded-lg border border-line bg-surface p-3.5" data-testid={`order-review-item-${item.product_id}`}>
              <p className="text-xs font-bold text-ink">{item.name}</p>
              {existing ? (
                <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-emerald-700" data-testid={`order-review-done-${item.product_id}`}>
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {existing.is_approved ? "Avis publié — merci !" : "Avis envoyé — en attente de publication"}
                </p>
              ) : (
                <>
                  <div className="mt-2 flex gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        data-testid={`order-review-star-${item.product_id}-${n}`}
                        onClick={() => setForm(item.product_id, { rating: n })}
                        className={`text-xl leading-none transition-colors duration-200 ${
                          n <= (form.rating || 5) ? "text-amber-400" : "text-slate-300"
                        }`}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                  <textarea
                    data-testid={`order-review-comment-${item.product_id}`}
                    rows={2}
                    value={form.comment || ""}
                    onChange={(e) => setForm(item.product_id, { comment: e.target.value })}
                    placeholder="Partagez votre expérience..."
                    className="mt-2 w-full rounded-lg border border-line p-2.5 text-xs focus:border-brand focus:outline-none"
                  />
                  <div className="mt-2">
                    {photos[item.product_id] ? (
                      <span className="flex items-center gap-2" data-testid={`order-review-photo-preview-${item.product_id}`}>
                        <img src={URL.createObjectURL(photos[item.product_id])} alt="Aperçu" className="h-10 w-10 rounded-lg border border-line object-cover" />
                        <span className="max-w-[120px] truncate text-[10px] text-slate-500">{photos[item.product_id].name}</span>
                        <button
                          type="button"
                          data-testid={`order-review-photo-remove-${item.product_id}`}
                          onClick={() => setPhotos((p) => ({ ...p, [item.product_id]: null }))}
                          className="text-[10px] font-bold text-rose-500 hover:underline"
                        >
                          Retirer
                        </button>
                      </span>
                    ) : (
                      <label className="flex w-fit cursor-pointer items-center gap-1.5 rounded-lg border border-dashed border-line px-2.5 py-1.5 text-[10px] font-bold text-slate-500 transition-colors duration-200 hover:border-brand hover:text-brand">
                        <ImagePlus className="h-3 w-3" />
                        Ajouter une photo (facultatif)
                        <input
                          type="file"
                          data-testid={`order-review-photo-input-${item.product_id}`}
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f && f.size > 5 * 1024 * 1024) return toast.error("Image trop lourde (max 5 Mo)");
                            if (f) setPhotos((p) => ({ ...p, [item.product_id]: f }));
                            e.target.value = "";
                          }}
                        />
                      </label>
                    )}
                  </div>
                  <button
                    data-testid={`order-review-submit-${item.product_id}`}
                    onClick={() => submit(item)}
                    disabled={sending === item.product_id}
                    className="mt-2 flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-xs font-bold text-white transition-colors duration-200 hover:bg-brand/90 disabled:opacity-60"
                  >
                    {sending === item.product_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Star className="h-3.5 w-3.5" />}
                    Publier mon avis
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
